import { ChartDataObject } from "@models/app/helpers.model";
import { IccMessage } from "@models/health/sleep-health-icc.model";
import { CircadianRhythm } from "@models/sessions/circadian-rhythm.model";
import { Rolling30DaysSession, SleepDataAggregates } from "@models/sessions/rolling-data-aggregates";
import { NoDataSession, SessionModel } from "@models/sessions/session.model";
import { SleepData, SleepDataSessions, SleepHistory, YearSleepDataEntityModel } from "@models/sessions/sleep-data.model";
import { Selector, createSelector } from "@ngxs/store";
import { SiqDateFormats } from "@shared/utils/helpers/date-formats.helper";
import { SleepDataType } from "@shared/utils/helpers/enum.helper";
import { SleepersStateModel } from "@store/sleepers/sleepers.model";
import { SleepersState } from "@store/sleepers/sleepers.state";
import * as moment from "moment";
import { SessionsStateModel } from "./sessions.model";
import { SessionsState } from "./sessions.state";

export class SessionsSelectors {

  @Selector([SessionsState])
  static loading(state: SessionsStateModel): boolean {
    return state.loading;
  }

  @Selector([SessionsState, SleepersState])
  static lastSessionFetched(state: SessionsStateModel, sleeperState: SleepersStateModel): boolean {
    return !!state.sleepData.find(s => s.sleeperId === sleeperState.selectedSleeper?.sleeperId)?.sleepData;
  }

  @Selector([SessionsState])
  static selectedSession(state: SessionsStateModel): SessionModel | NoDataSession | null {
    return state.selectedSession;
  }

  @Selector([SessionsState, SleepersState])
  static latestSleepNumberSetting(state: SessionsStateModel, sleeperState: SleepersStateModel): number | undefined | null {
    const sessions = state.sleepData.find(s => s.sleeperId === sleeperState?.selectedSleeper?.sleeperId)?.sleepData;
    if (sessions) {
      const mergedSleepSessions = this.mergeSessions(sessions);
      return mergedSleepSessions.find(session => session.sleepNumber)?.sleepNumber;
    }
    return 0;
  }


  @Selector([SessionsState, SleepersState])
  static circadianRhythm(state: SessionsStateModel, sleeperState: SleepersStateModel): CircadianRhythm | null {
    const originalStartDate = state.selectedSession?.originalStartDate;
    const originalEndDate = state.selectedSession?.originalEndDate;
    const sleeperCr = state.circadianRhythm.find((cr) => cr.sleeperId === sleeperState.selectedSleeper?.sleeperId && cr.circadianRhythm.originalSleepSession.start === originalStartDate && cr.circadianRhythm.originalSleepSession.end === originalEndDate);
    return sleeperCr ? sleeperCr.circadianRhythm : null;
  }

  static getIccMessageForCategory(category: string): (state: SessionsStateModel) => IccMessage | null {
    return createSelector([SessionsState], (sessionState: SessionsStateModel) => {
      const iccMessages: IccMessage[] | undefined = sessionState.iccMessages.find((icc) =>
        icc.startDate === sessionState?.selectedSession?.startDate && icc.endDate === sessionState.selectedSession.endDate
      )?.messages;
      return iccMessages ? (iccMessages as Record<string, any>)[category] : null;
    });
  }

  @Selector([SessionsState, SleepersState])
  static rolling30Days(state: SessionsStateModel, sleeperState: SleepersStateModel): Rolling30DaysSession | null {
    const selectedSleeper30DaysRolling = state.rolling30Days.find((rolling30Days) => rolling30Days.sleeperId === sleeperState.selectedSleeper?.sleeperId);
    // since we are only fetching one day, there will always be one element in an array
    const rollingData = selectedSleeper30DaysRolling?.sleepData[0].sessions.find((it) => it.originalStartDate === state.selectedSession?.originalStartDate && it.originalEndDate === state.selectedSession.originalEndDate);
    return rollingData ? rollingData : null;
  }

  @Selector([SessionsState, SleepersState])
  static rollingDataAggregates(state: SessionsStateModel, sleeperState: SleepersStateModel): SleepDataAggregates | null {
    const selectedSleeperRollingDataAggregates = state.rollingDataAggregates.find((rollingAggregates) => rollingAggregates.sleeperId === sleeperState.selectedSleeper?.sleeperId);
    return selectedSleeperRollingDataAggregates ? selectedSleeperRollingDataAggregates.sleepDataAggregates : null;
  }

  static sleepHistory(type: SleepDataType, startOf: string): (sessionsState: SessionsStateModel, sleeperState: SleepersStateModel) => Array<SleepHistory> {
    return createSelector([SessionsState, SleepersState], (sessionsState: SessionsStateModel, sleeperState: SleepersStateModel): Array<SleepHistory> => {
      const sleeperId = sleeperState.selectedSleeper?.sleeperId;
      const property = type === SleepDataType.Month ? 'sleepData' : 'weekData';
      const timeFrame = type === SleepDataType.Month ? 'month' : 'week';
      const sleeperData = sessionsState[property].find((data) => data.sleeperId === sleeperId);
      const sleeperSleepDataFiltered = sleeperData?.sleepData.filter((data) => moment(data.date).isSame(startOf, timeFrame))[0];
      const sleeperSleepData = sleeperSleepDataFiltered ? JSON.parse(JSON.stringify(sleeperSleepDataFiltered)) : null;
      if (sleeperSleepData?.sleepData?.length) {
        sleeperSleepData.sleepData = sleeperSleepData.sleepData.reverse();
      }
      return sleeperSleepData && sleeperSleepData.sleepData.length > 0 ? [new SleepHistory(startOf, sleeperSleepData.sleepData, sleeperSleepData.sleepIQAvg, sleeperSleepData.sleepIQMax, sleeperSleepData.inBedAvg)] : [new SleepHistory(startOf, [], 0, 0, 0)];
    });
  }

  @Selector([SessionsState, SleepersState])
  static yearSleepData(state: SessionsStateModel, sleepersState: SleepersStateModel): Array<YearSleepDataEntityModel> {
    return state.sleepDataYear.filter(data => data.sleeperId === sleepersState.selectedSleeper?.sleeperId);
  }

  static rollingDays(period: number): (sessionState: SessionsStateModel, sleeperState: SleepersStateModel) => Array<SessionModel | NoDataSession> | null {
    return createSelector([SessionsState, SleepersState], (sessionState: SessionsStateModel, sleeperState: SleepersStateModel): Array<SessionModel | NoDataSession> | null => {
      const sleeperSessions: Array<SleepData> = JSON.parse(JSON.stringify(sessionState.sleepData.find(s => s.sleeperId === sleeperState?.selectedSleeper?.sleeperId)?.sleepData));
      let mergedSleepSessions: Array<SleepDataSessions> = [];
      sleeperSessions.map((it) => {
        mergedSleepSessions = mergedSleepSessions.concat(it.sleepData);
      });
      mergedSleepSessions = mergedSleepSessions.filter((ss) => moment(ss.date).isSameOrBefore(sessionState.selectedSession?.date));
      const periodData = mergedSleepSessions?.splice(0, period).reverse();
      const rollingDaysData = periodData.map((it) => {
        if (it.sessions.length > 0) {
          const longestSleepSession = it.sessions.find((ss) => ss.longest);
          if (longestSleepSession) {
            return new SessionModel(longestSleepSession);
          }
        }
        return new NoDataSession(moment(it.date).subtract(1, 'day').format(SiqDateFormats.ISO8601), moment(it.date).format(SiqDateFormats.ISO8601));
      });
      if (rollingDaysData.length < period) {
        while (rollingDaysData.length < period) {
          rollingDaysData.unshift(new NoDataSession(moment(rollingDaysData[0].date).format(SiqDateFormats.ISO8601), moment(rollingDaysData[0].date).subtract(1, 'day').format(SiqDateFormats.ISO8601)));
        }
      }
      return rollingDaysData;
    });
  }

  static mapBiosignalChartData(selectedTab: SleepDataType, metric: string): (sessionState: SessionsStateModel, sleeperState: SleepersStateModel) => Array<ChartDataObject> {
    return createSelector([SessionsState, SleepersState], (sessionState: SessionsStateModel, sleeperState: SleepersStateModel): Array<ChartDataObject> => {
      const rollingDays = selectedTab === SleepDataType.Week ? this.rollingDays(7)(sessionState, sleeperState) : this.rollingDays(30)(sessionState, sleeperState);
      const chartData: Array<ChartDataObject> = [];
      if (rollingDays) {
        rollingDays.forEach(item => {
          chartData.push(Object.assign(new ChartDataObject(), {
            date: item.date,
            value: this.getSessionValue(item, metric)
          }));
        });
      }

      return chartData;
    });
  }

  //#region Private functions
  private static mergeSessions(sleepData: Array<SleepData>): Array<SessionModel> {
    let mergedSleepSessions: Array<SessionModel> = []; // only data sessions
    sleepData.forEach((ss) => {
      ss.sleepData.forEach((it) => {
        mergedSleepSessions = mergedSleepSessions.concat(it.sessions);
      });
    });
    return mergedSleepSessions;
  }


  private static getSessionValue(session: SessionModel | NoDataSession, prop: string): number {
    if (session.isSession) {
      const tmp = session as SessionModel;
      if (prop === 'hrv') {
        const hrv = tmp.hrv;
        if (tmp.hrvActionCode !== null || hrv < 0) {
          return 0;
        }
        return hrv;
      } else {
        const propValue = tmp[prop as keyof SessionModel];
        if(typeof propValue === 'number')
          return propValue < 0 ? 0 : propValue;
      }
    }
    return 0;
  }
  //#endregion
}